/**
 * Entity Context Eligibility
 * 
 * Centralized logic for determining which entities should be added to AI context
 * when opened or clicked in the sidebar.
 * 
 * PRINCIPLE: If it's in the sidebar and can be opened → It's context-eligible
 */

import { getAppTypeForEntity } from './entityTypeMapping';

/**
 * Determine if an entity should be added to context when opened
 * 
 * RULE: Any entity with an llmId is eligible, EXCEPT:
 * - Groups (folder containers only)
 * - Spaces (navigation only)
 */
export function isContextEligibleEntity(entity: any): boolean {
  if (!entity) return false;
  
  // Must have llmId to be AI-accessible
  if (!entity.llmId) return false;
  
  // Exclude container/navigation-only entities and app builder projects.
  // AppBuilder projects self-manage their context via setEntityContextContribution
  // (entityContexts), so _handleActiveWindowChange must not also add them as
  // currentDoc document references — that would cause duplicate context items.
  const excludedTypes = ['Group', 'Space', 'AppProject', 'app_project', 'WebsiteProject', 'website_project', 'RiverAppStorage'];
  if (excludedTypes.includes(entity.entityType)) return false;
  
  // Only allow entity types the AI can actually read or interact with.
  // This blocks custom apps (WritersDashboard) and binary
  // file types (images, video, audio, archives) from appearing in context.
  if (!isAIReadableEntityType(entity.entityType)) return false;
  
  return true;
}

/**
 * Get the appropriate appType for context storage
 * Uses centralized entity type mapping
 */
export function getContextAppType(entity: any): string {
  return getAppTypeForEntity(entity);
}

/**
 * Determine if an entity should have tools loaded for AI interaction
 * 
 * Entities eligible for tools:
 * - All v2 entities (use entity_read_content/entity_write)
 * - All v1 entities (use legacy commands)
 * - PDFs (use pdfium commands)
 * - External integrations (use integration-specific commands)
 */
export function shouldLoadToolsForEntity(entity: any): boolean {
  if (!isContextEligibleEntity(entity)) return false;
  
  const appType = getContextAppType(entity);
  
  // Define app types that have AI tools
  const toolEnabledAppTypes = [
    // V1 apps
    'docs',
    'notes-markdown',
    'pdfium',

    // V2 apps
    'docs-v2',
    'notes-v2',
    'sheets-v2',
    'presentations-v2',
    'canvas-v2',

    // Book Formatter
    'book_formatter_v2',
    'book-formatter-v2',

    // Report apps
    'manuscript-review-report',

    // Other
    'tasks',
    'entity-view',
    'google-drive-file',
  ];
  
  return toolEnabledAppTypes.includes(appType);
}

/**
 * Get the command set key for tool loading
 * Maps appType → command registry key
 */
export function getCommandSetForAppType(appType: string): string | null {
  const appTypeToCommandSet: Record<string, string> = {
    // V1 apps
    'docs': 'docs',
    'notes-markdown': 'notes',
    'pdfium': 'pdf',
    
    // V2 apps - all use universal commands
    'docs-v2': 'docs',  // Docs v2 supports both doc_read_v2 and entity_read_content
    'notes-v2': 'universal',
    'sheets-v2': 'universal',
    'presentations-v2': 'universal',
    'canvas-v2': 'universal',
    
    // Report apps - read-only, use entity_read_content via universal commands
    'manuscript-review-report': 'universal',

    // Book Formatter
    'book_formatter_v2': 'book',
    'book-formatter-v2': 'book',

    // Other
    'tasks': 'tasks',
    'entity-view': 'universal',
    'google-drive-file': 'google-drive',
  };
  
  return appTypeToCommandSet[appType] || null;
}

/**
 * Determine if an entity type can be read or interacted with by the AI.
 * 
 * Only entity types that pass this check should appear in AI context blocks
 * (USER_DOCUMENTS, CONTEXT_ENTITIES, entity info blocks). Anything the AI
 * cannot read — custom apps, images, video, audio, archives — is excluded.
 */
export function isAIReadableEntityType(entityType: string | undefined): boolean {
  if (!entityType) return false;

  const readableTypes = [
    // Core content types
    'Doc', 'Document',
    'Note', 'Content.Note',
    'Canvas',
    'Sheet', 'Spreadsheet',
    'Presentation', 'Slides', 'Slide',
    'PDF', 'File.Document.PDF',
    'ManuscriptReviewReport',
    'Dashboard', 'dashboard',
    'BookProject',
    'Group',
    // Task management entities (have dedicated tasks command set)
    'Project', 'Area',
    // API connection entities (serviceProfile is readable documentation)
    'APIConnection',
    // Google Drive file entities (have dedicated google-drive command set)
    'GoogleDriveFile',
    // App builder projects (automatically injected as current window context)
    'AppProject', 'app_project',
    'WebsiteProject', 'website_project',
    // Automation entities — shown as active context when the user views them
    'Automation', 'Agent',
  ];
  if (readableTypes.includes(entityType)) return true;

  return (
    entityType.startsWith('Content.Note.') ||
    entityType.startsWith('File.Document.') ||
    entityType.startsWith('File.Code.') ||
    entityType.startsWith('File.Data.') ||
    entityType.startsWith('File.Spreadsheet.') ||
    entityType.startsWith('File.Presentation.') ||
    // Google Drive hierarchical types (External.GoogleDrive.Document, etc.)
    entityType.startsWith('External.GoogleDrive.')
  );
}

/**
 * Get display name for entity type (for UI)
 */
export function getEntityTypeDisplayName(entityType: string): string {
  const displayNames: Record<string, string> = {
    'Doc': 'Document',
    'Note': 'Note',
    'Sheet': 'Spreadsheet',
    'Spreadsheet': 'Spreadsheet',
    'Presentation': 'Presentation',
    'Slides': 'Presentation',
    'Canvas': 'Canvas',
    'PDF': 'PDF',
    'File.Document.PDF': 'PDF',
    'Task': 'Task',
    'Project': 'Project',
    'Area': 'Area',
    'Thread': 'Thread',
  };
  
  return displayNames[entityType] || entityType;
}

